import React from "react";
import { Link, useParams } from "react-router-dom";
import { 
  Globe, Palette, TrendingUp, Monitor, Layout, Brush, 
  FileText, Users, Target, Search, Cog, Smartphone, Phone,
  ArrowRight, ArrowLeft, CheckCircle2, CreditCard, Flag, Calendar, Camera
} from "lucide-react";

const ServicesPage = () => {
  const { category } = useParams();

  const poles = [
    {
      id: "visibilite",
      icon: <Globe className="w-10 h-10" />,
      title: "Visibilité",
      tagline: "Augmentez votre présence digitale",
      heroText: "Être visible, c'est exister. Dans un monde digital saturé, votre présence en ligne détermine votre succès.",
      description: "Nous développons des stratégies de visibilité sur-mesure pour positionner votre marque là où se trouvent vos clients. Du site web performant au référencement naturel, en passant par les réseaux sociaux, nous créons un écosystème digital cohérent qui amplifie votre rayonnement.",
      heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      benefits: [
        "Augmentation du trafic qualifié",
        "Meilleur positionnement Google",
        "Présence sociale engageante",
        "Notoriété de marque renforcée"
      ],
      services: [
        {
          icon: <Monitor className="w-6 h-6" />,
          title: "Création de sites web",
          subtitle: "Vitrine & e-commerce",
          description: "Sites vitrines, e-commerce, refonte, UX/UI design, optimisation mobile, maintenance & mises à jour."
        },
        {
          icon: <Search className="w-6 h-6" />,
          title: "SEO & visibilité web",
          subtitle: "Référencement naturel",
          description: "Audit SEO, optimisation site, rédaction d'articles, netlinking, stratégie mots-clés."
        },
        {
          icon: <Users className="w-6 h-6" />,
          title: "Social Media Management",
          subtitle: "Gestion de communauté",
          description: "Stratégie éditoriale, création de contenus, gestion LinkedIn/Instagram/Facebook, community management."
        },
        {
          icon: <Smartphone className="w-6 h-6" />,
          title: "Développement applications",
          subtitle: "Solutions digitales",
          description: "Applications mobiles, web apps, outils métiers, plateformes internes."
        }
      ],
      color: "blue"
    },
    {
      id: "image-de-marque",
      icon: <Palette className="w-10 h-10" />,
      title: "Image de Marque",
      tagline: "Créez une identité unique et mémorable",
      heroText: "Votre image de marque est votre signature. Elle raconte qui vous êtes avant même que vous ne parliez.",
      description: "Nous créons des identités visuelles distinctives qui capturent l'essence de votre entreprise. Du logo aux supports commerciaux, chaque élément est conçu pour créer une expérience de marque cohérente et mémorable qui vous différencie de vos concurrents.",
      heroImage: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&q=80",
      benefits: [
        "Identité visuelle distinctive",
        "Cohérence sur tous les supports",
        "Perception premium de votre marque",
        "Mémorabilité accrue"
      ],
      services: [
        {
          icon: <Brush className="w-6 h-6" />,
          title: "Branding & identité visuelle",
          subtitle: "Positionnement stratégique",
          description: "Logo, charte graphique, univers visuel, naming, positionnement de marque, kits réseaux sociaux."
        },
        {
          icon: <FileText className="w-6 h-6" />,
          title: "Supports commerciaux",
          subtitle: "Corporate & vente",
          description: "Pitch decks, présentations commerciales, plaquettes, catalogues, fiches produits, flyers."
        },
        {
          icon: <Palette className="w-6 h-6" />,
          title: "Contenus visuels",
          subtitle: "Création graphique",
          description: "Visuels réseaux sociaux, infographies, illustrations, templates personnalisés, bannières web."
        },
        {
          icon: <CreditCard className="w-6 h-6" />,
          title: "Papeterie professionnelle",
          subtitle: "Identité corporate",
          description: "Cartes de visite, signatures email, en-têtes, papier à lettres, enveloppes personnalisées."
        }
      ],
      complementaryServices: [
        {
          icon: <Flag className="w-6 h-6" />,
          title: "Supports événementiels",
          subtitle: "PLV & signalétique",
          description: "Roll-ups, kakémonos, badges, totems, stands et habillage de stands.",
          price: "300"
        },
        {
          icon: <Calendar className="w-6 h-6" />,
          title: "Kit communication événement",
          subtitle: "Pack complet",
          description: "Invitations, programmes, signalétique, goodies personnalisés.",
          price: "450"
        },
        {
          icon: <Camera className="w-6 h-6" />,
          title: "Couverture social media événement",
          subtitle: "Live & contenu",
          description: "Live posting, stories, création de contenu sur site, reels récap.",
          price: "350"
        }
      ],
      color: "purple"
    },
    {
      id: "croissance",
      icon: <TrendingUp className="w-10 h-10" />,
      title: "Croissance",
      tagline: "Générez des leads et accélérez vos ventes",
      heroText: "La croissance n'est pas un hasard. C'est le résultat d'une stratégie précise et d'actions ciblées.",
      description: "Nous mettons en place des systèmes d'acquisition performants pour générer un flux constant de prospects qualifiés. De la publicité digitale aux tunnels de vente automatisés, nous optimisons chaque étape de votre parcours client pour maximiser votre ROI.",
      heroImage: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
      benefits: [
        "Génération de leads qualifiés",
        "Augmentation du taux de conversion",
        "Automatisation des processus",
        "ROI mesurable et optimisé"
      ],
      services: [
        {
          icon: <Target className="w-6 h-6" />,
          title: "Acquisition digitale",
          subtitle: "Paid Ads",
          description: "Google Ads, Meta Ads, LinkedIn Ads, retargeting, optimisation ROI."
        },
        {
          icon: <Layout className="w-6 h-6" />,
          title: "Landing pages & tunnels",
          subtitle: "Optimisation de conversion",
          description: "Landing pages marketing, pages de capture, tunnel de vente, A/B testing, optimisation CRO."
        },
        {
          icon: <Cog className="w-6 h-6" />,
          title: "Marketing automation",
          subtitle: "CRM & automation",
          description: "Email marketing, scénarios automatisés, funnels CRM, HubSpot/Salesforce, lead nurturing."
        },
        {
          icon: <Phone className="w-6 h-6" />,
          title: "Génération de leads",
          subtitle: "Prospection B2B",
          description: "Cold calling, prospection LinkedIn, scripts commerciaux, emailing B2B, prise de rendez-vous."
        }
      ],
      color: "green"
    }
  ];

  // Find current pole if category is specified
  const currentPole = category ? poles.find(p => p.id === category) : null;

  const getColorClasses = (color, type) => {
    const colors = {
      blue: {
        bg: "bg-blue-500",
        bgLight: "bg-blue-100",
        text: "text-blue-600",
        border: "border-blue-500",
        gradient: "from-blue-500 to-cyan-500"
      },
      purple: {
        bg: "bg-purple-500",
        bgLight: "bg-purple-100",
        text: "text-purple-600",
        border: "border-purple-500",
        gradient: "from-purple-500 to-pink-500"
      },
      green: {
        bg: "bg-emerald-500",
        bgLight: "bg-emerald-100",
        text: "text-emerald-600",
        border: "border-emerald-500",
        gradient: "from-emerald-500 to-teal-500"
      }
    };
    return colors[color][type];
  };

  // Render individual pole page
  if (currentPole) {
    return (
      <div className="pt-20">
        {/* Hero avec image */}
        <section className={`py-24 bg-gradient-to-br ${getColorClasses(currentPole.color, 'gradient')} overflow-hidden`} data-testid={`pole-hero-${currentPole.id}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
              data-testid="back-to-services"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour aux services
            </Link>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-white">
                    {currentPole.icon}
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-medium uppercase tracking-wider">{currentPole.tagline}</p>
                    <h1 className="text-4xl md:text-5xl font-bold text-white">{currentPole.title}</h1>
                  </div>
                </div>
                
                <p className="text-2xl md:text-3xl font-playfair text-white/90 italic max-w-xl mt-8">
                  "{currentPole.heroText}"
                </p>
              </div>
              
              <div className="relative">
                <img
                  src={currentPole.heroImage}
                  alt={currentPole.title}
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full ${getColorClasses(currentPole.color, 'bgLight')} flex items-center justify-center`}>
                      <span className={`text-2xl font-bold ${getColorClasses(currentPole.color, 'text')}`}>{currentPole.services.length}</span>
                    </div>
                    <div>
                      <div className="text-gray-900 font-semibold">Services</div>
                      <div className="text-gray-600 text-sm">spécialisés</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="py-20 bg-white" data-testid="pole-description">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Notre approche {currentPole.title}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {currentPole.description}
                </p>
                
                <div className="space-y-4">
                  {currentPole.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className={`w-6 h-6 ${getColorClasses(currentPole.color, 'text')}`} />
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={`rounded-3xl p-10 bg-gradient-to-br ${getColorClasses(currentPole.color, 'gradient')} text-white`}>
                <div className="text-6xl font-bold mb-4">{currentPole.services.length}</div>
                <div className="text-xl font-medium mb-2">Services spécialisés</div>
                <p className="text-white/80">
                  Pour maximiser votre {currentPole.title.toLowerCase()} et atteindre vos objectifs
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services included */}
        <section className="py-20 bg-gray-50" data-testid="pole-services">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Services inclus dans le pôle {currentPole.title}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Une gamme complète de services pour répondre à tous vos besoins en {currentPole.title.toLowerCase()}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {currentPole.services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                  data-testid={`service-${index}`}
                >
                  <div className={`w-14 h-14 rounded-xl ${getColorClasses(currentPole.color, 'bgLight')} ${getColorClasses(currentPole.color, 'text')} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className={`${getColorClasses(currentPole.color, 'text')} font-medium text-sm mb-4`}>{service.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Complementary Services (Event services for Image de Marque) */}
        {currentPole.complementaryServices && (
          <section className="py-20 bg-white" data-testid="complementary-services">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <span className={`${getColorClasses(currentPole.color, 'text')} font-semibold text-sm uppercase tracking-wider`}>Services complémentaires</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
                  Événementiel & Communication
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Prolongez votre identité de marque lors de vos événements avec nos services dédiés
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {currentPole.complementaryServices.map((service, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                    data-testid={`complementary-service-${index}`}
                  >
                    <div className={`w-14 h-14 rounded-xl ${getColorClasses(currentPole.color, 'bgLight')} ${getColorClasses(currentPole.color, 'text')} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className={`${getColorClasses(currentPole.color, 'text')} font-medium text-sm mb-4`}>{service.subtitle}</p>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link
                  to="/offres/services-carte"
                  className={`inline-flex items-center gap-2 px-6 py-3 ${getColorClasses(currentPole.color, 'bg')} text-white font-semibold rounded-full hover:opacity-90 transition-all duration-300`}
                >
                  Voir tous les services à la carte
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className={`py-20 bg-gradient-to-br ${getColorClasses(currentPole.color, 'gradient')}`} data-testid="pole-cta">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à booster votre {currentPole.title.toLowerCase()} ?
            </h2>
            <p className="text-white/80 text-lg mb-10">
              Discutons de votre projet et définissons ensemble la meilleure stratégie
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg"
                data-testid="pole-cta-contact"
              >
                Demander un devis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/offres"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                data-testid="pole-cta-offers"
              >
                Voir nos offres
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Main services page
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-cyan-600" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-cyan-100 font-medium text-sm uppercase tracking-wider">Nos Services</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Comment pouvons-nous vous aider ?
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Une approche structurée en 3 pôles stratégiques pour maximiser votre impact
          </p>
        </div>
      </section>

      {/* 3 Poles */}
      <section className="py-24 bg-white" data-testid="poles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              3 Pôles Stratégiques
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Chaque pôle répond à un besoin spécifique de votre développement. Découvrez nos expertises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {poles.map((pole) => (
              <div
                key={pole.id}
                className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group"
                data-testid={`pole-card-${pole.id}`}
              >
                <div className={`w-20 h-20 rounded-2xl ${getColorClasses(pole.color, 'bgLight')} flex items-center justify-center ${getColorClasses(pole.color, 'text')} mb-6 group-hover:scale-110 transition-transform`}>
                  {pole.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{pole.title}</h3>
                <p className="text-gray-600 mb-4">{pole.tagline}</p>
                
                <p className="text-gray-500 text-sm mb-6 line-clamp-3">
                  {pole.heroText}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {pole.services.slice(0, 3).map((service) => (
                    <span key={service.title} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {service.title.split(' ')[0]}
                    </span>
                  ))}
                  {pole.services.length > 3 && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      +{pole.services.length - 3}
                    </span>
                  )}
                </div>

                <Link
                  to={`/services/${pole.id}`}
                  className={`inline-flex items-center gap-2 px-6 py-3 ${getColorClasses(pole.color, 'bg')} text-white font-semibold rounded-full hover:opacity-90 transition-all group-hover:gap-3`}
                  data-testid={`discover-${pole.id}`}
                >
                  Découvrir
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview of all services */}
      <section className="py-20 bg-gray-50" data-testid="all-services-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous nos services en un coup d'œil
            </h2>
            <p className="text-gray-600">
              {poles.reduce((acc, pole) => acc + pole.services.length, 0)} services répartis sur nos 3 pôles d'expertise
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {poles.map((pole) => (
              <div key={pole.id} className="space-y-4">
                <div className={`flex items-center gap-3 pb-4 border-b-2 ${getColorClasses(pole.color, 'border')}`}>
                  <div className={`w-10 h-10 rounded-lg ${getColorClasses(pole.color, 'bgLight')} ${getColorClasses(pole.color, 'text')} flex items-center justify-center`}>
                    {React.cloneElement(pole.icon, { className: "w-5 h-5" })}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{pole.title}</h3>
                </div>
                {pole.services.map((service, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className={`w-8 h-8 rounded-lg ${getColorClasses(pole.color, 'bgLight')} ${getColorClasses(pole.color, 'text')} flex items-center justify-center flex-shrink-0`}>
                      {React.cloneElement(service.icon, { className: "w-4 h-4" })}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{service.title}</p>
                      <p className="text-gray-500 text-xs">{service.subtitle}</p>
                    </div>
                  </div>
                ))}
                <Link 
                  to={`/services/${pole.id}`}
                  className={`inline-flex items-center gap-2 ${getColorClasses(pole.color, 'text')} font-semibold text-sm hover:gap-3 transition-all`}
                >
                  Voir les détails <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Vous ne savez pas par où commencer ?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Nos experts analysent votre situation et vous recommandent les services adaptés à vos objectifs
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-white font-semibold rounded-full hover:bg-cyan-400 transition-all duration-300 shadow-lg"
            data-testid="services-cta-button"
          >
            Réserver un appel stratégique gratuit
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
